import { paginatedQuery } from 'lib-shared-api-utils';

import { api } from 'app/app.api';

import {
  CreateFilterApiArg,
  CreateFilterApiResponse,
  CreateOrUpdateSurveyLanguageSpecificDetailsApiArg,
  CreateRatingSettingsApiArg,
  CreateRatingSettingsResponse,
  CreateSurveyApiArg,
  CreateSurveyApiResponse,
  CreateUserExclusionApiArg,
  DeleteFilterApiArg,
  DeleteRatingSettingsApiArg,
  DeleteRatingSettingsResponse,
  DeleteSurveyApiArg,
  DeleteSurveyLanguageSpecificDetailsApiArg,
  DeleteUserExclusionApiArg,
  GetFutureRatingInfoApiArg,
  GetFutureRatingInfoApiResponse,
  GetRatingSettingsApiArg,
  GetRatingSettingsResponse,
  LinkRatingSettingsApiArg,
  LinkRatingSettingsResponse,
  ReadFilterByIdApiArg,
  ReadFilterByIdApiResponse,
  ReadFilterContactPointsApiResponse,
  ReadFiltersApiResponse,
  ReadRatingSettingsApiArg,
  ReadRatingSettingsLinksApiArg,
  ReadRatingSettingsLinksResponse,
  ReadRatingSettingsResponse,
  ReadSatisfactionChannelDisplayNamesApiResponse,
  ReadSurveyAndOutgoingChannelLinkApiArg,
  ReadSurveyAndOutgoingChannelLinkApiResponse,
  ReadSurveyByIdApiArg,
  ReadSurveyByIdApiResponse,
  ReadSurveyConnectionsApiArg,
  ReadSurveyConnectionsResponse,
  ReadSurveyLanguageSpecificDetailsApiArg,
  ReadSurveyLanguageSpecificDetailsApiResponse,
  ReadSurveysApiResponse,
  ReadSurveyTypesApiResponse,
  ReadUserExclusionByIdApiArg,
  ReadUserExclusionByIdApiResponse,
  ReadUserExclusionsApiArg,
  ReadUserExclusionsApiResponse,
  UnlinkRatingSettingsApiArg,
  UnlinkRatingSettingsResponse,
  UpdateFilterApiArg,
  UpdateFilterOrderApiArg,
  UpdateRatingSettingsApiArg,
  UpdateRatingSettingsResponse,
  UpdateSurveyNameApiArg,
} from './types/ratingApi.types';
import { transformFilterApiArg } from './utils/transformArg';
import { transformReadFilterByIdApiResponse } from './utils/transformResponse';

export const ratingApi = api
  .enhanceEndpoints({
    addTagTypes: [
      'Surveys',
      'SurveyLanguageSpecificDetails',
      'CsatExclusion',
      'CsatFilters',
      'RatingInfo',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readRatingSettings: builder.query<
        ReadRatingSettingsResponse,
        ReadRatingSettingsApiArg
      >({
        query: ({ level, surveyType, parentId, language }) => ({
          version: 2,
          path: 'ratings/settings',
          params: { level, surveyType, parentId, language },
          method: 'GET',
        }),
      }),
      getRatingSettings: builder.query<
        GetRatingSettingsResponse,
        GetRatingSettingsApiArg
      >({
        query: ({ ratingSettingsId }) => ({
          version: 2,
          path: `ratings/settings/${ratingSettingsId}`,
          method: 'GET',
        }),
      }),
      getFutureRatingInfo: builder.query<
        GetFutureRatingInfoApiResponse,
        GetFutureRatingInfoApiArg
      >({
        query: (arg) => ({
          version: 1,
          path: 'ratings/information',
          method: 'POST',
          body: {
            conversation_id: arg.csid,
          },
        }),
        providesTags: ['RatingInfo'],
      }),
      deleteRatingSettings: builder.query<
        DeleteRatingSettingsResponse,
        DeleteRatingSettingsApiArg
      >({
        query: ({ ratingSettingsId }) => ({
          version: 2,
          path: `ratings/settings/${ratingSettingsId}`,
          method: 'DELETE',
        }),
      }),
      createRatingSettings: builder.query<
        CreateRatingSettingsResponse,
        CreateRatingSettingsApiArg
      >({
        query: (arg) => ({
          version: 2,
          path: 'ratings/settings',
          method: 'POST',
          body: arg,
        }),
      }),
      //v3, which is not compatible with v2
      linkRatingSettings: builder.query<
        LinkRatingSettingsResponse,
        LinkRatingSettingsApiArg
      >({
        query: (arg) => ({
          version: 3,
          path: `ratings/settings/links`,
          method: 'POST',
          body: {
            ratingSettingsId: arg.rating_settings_id,
            contactPoint: arg.contact_point,
            contactPointKind: arg.contact_point_kind,
            additionalSettings: arg.additional_settings,
          },
        }),
      }),
      unlinkRatingSettings: builder.query<
        UnlinkRatingSettingsResponse,
        UnlinkRatingSettingsApiArg
      >({
        query: (arg) => ({
          version: 3,
          path: `ratings/settings/links`,
          method: 'DELETE',
          body: {
            ratingSettingsId: arg.rating_settings_id,
            contactPoint: arg.contact_point,
            contactPointKind: arg.contact_point_kind,
          },
        }),
      }),
      readRatingSettingsLinks: builder.query<
        ReadRatingSettingsLinksResponse,
        ReadRatingSettingsLinksApiArg
      >({
        query: ({ contactPointKind }) => ({
          version: 3,
          path: 'ratings/settings/links',
          method: 'GET',
          params: {
            contactPointKind,
          },
        }),
      }),
      updateRatingSettings: builder.query<
        UpdateRatingSettingsResponse,
        UpdateRatingSettingsApiArg
      >({
        query: (arg) => ({
          version: 2,
          path: `ratings/settings/${arg.id}`,
          method: 'PUT',
          body: arg,
        }),
      }),
      // Read a list of all connected phone numbers to a Survey
      readSurveyConnections: builder.query<
        ReadSurveyConnectionsResponse,
        ReadSurveyConnectionsApiArg
      >({
        query: ({ ratingSettingId }) => ({
          version: 3,
          path: 'ratings/settings/links',
          params: { ratingSettingId },
          method: 'GET',
        }),
      }),
      readSatisfactionFilters: builder.query<ReadFiltersApiResponse, void>({
        query: () => ({
          version: 1,
          path: 'ratings/filters',
          method: 'GET',
        }),
        providesTags: ['CsatFilters'],
      }),
      readSatisfactionFilterById: builder.query<
        ReadFilterByIdApiResponse,
        ReadFilterByIdApiArg
      >({
        query: ({ filterId }) => ({
          version: 1,
          path: `ratings/filters/${filterId}`,
          method: 'GET',
        }),
        transformResponse: transformReadFilterByIdApiResponse,
        providesTags: ['CsatFilters'],
      }),
      createSatisfactionFilter: builder.mutation<
        CreateFilterApiResponse,
        CreateFilterApiArg
      >({
        query: ({ filter }) => ({
          version: 1,
          path: 'ratings/filters',
          method: 'POST',
          body: transformFilterApiArg(filter),
        }),
        invalidatesTags: ['CsatFilters'],
      }),
      updateSatisfactionFilter: builder.mutation<void, UpdateFilterApiArg>({
        query: ({ filterId, filter }) => ({
          version: 1,
          path: `ratings/filters/${filterId}`,
          method: 'PUT',
          body: transformFilterApiArg(filter),
        }),
        invalidatesTags: ['CsatFilters'],
      }),
      deleteSatisfactionFilter: builder.mutation<void, DeleteFilterApiArg>({
        query: ({ filterId }) => ({
          version: 1,
          path: `ratings/filters/${filterId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['CsatFilters'],
      }),
      updateSatisfactionFilterOrder: builder.mutation<
        void,
        UpdateFilterOrderApiArg
      >({
        query: ({ filterIds }) => ({
          version: 1,
          path: 'ratings/filters/order',
          method: 'PUT',
          body: {
            order: filterIds,
          },
        }),
        invalidatesTags: ['CsatFilters'],
      }),
      readSatisfactionFilterContactPoints: builder.query<
        ReadFilterContactPointsApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'ratings/contact-points',
          method: 'GET',
        }),
      }),
      readSatisfactionChannelDisplayNames: builder.query<
        ReadSatisfactionChannelDisplayNamesApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          // The actual language selection happens with the ISO_639-3 property
          path: 'channels?ISO_639-3=eng',
          method: 'GET',
        }),
      }),
      readSurveyAndOutgoingChannelLink: builder.query<
        ReadSurveyAndOutgoingChannelLinkApiResponse,
        ReadSurveyAndOutgoingChannelLinkApiArg
      >({
        query: ({ surveyId, channel }) => ({
          version: 1,
          path: `ratings/surveys/${surveyId}/additional_data?channel=${channel}`,
          method: 'GET',
        }),
      }),
      readUserExclusions: builder.query<
        ReadUserExclusionsApiResponse,
        ReadUserExclusionsApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, () => ({
            version: 1,
            path: 'ratings/exclusions',
            method: 'GET',
            params: { search: arg.searchTerm },
          })),
        providesTags: ['CsatExclusion'],
      }),
      readUserExclusionById: builder.query<
        ReadUserExclusionByIdApiResponse,
        ReadUserExclusionByIdApiArg
      >({
        query: ({ userId }) => ({
          version: 1,
          path: `ratings/exclusions/${userId}`,
          method: 'GET',
        }),
        providesTags: ['CsatExclusion'],
      }),
      createUserExclusion: builder.mutation<void, CreateUserExclusionApiArg>({
        query: ({ userId }) => ({
          version: 1,
          path: 'ratings/exclusions',
          method: 'POST',
          body: { user_id: userId },
        }),
        invalidatesTags: ['CsatExclusion', 'RatingInfo'],
      }),
      deleteUserExclusion: builder.mutation<void, DeleteUserExclusionApiArg>({
        query: ({ userId }) => ({
          version: 1,
          path: `ratings/exclusions/${userId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['CsatExclusion', 'RatingInfo'],
      }),
      readSurveys: builder.query<ReadSurveysApiResponse, void>({
        query: () => ({
          version: 1,
          path: 'ratings/surveys',
          method: 'GET',
        }),
        providesTags: ['Surveys'],
      }),
      readSurveysById: builder.query<
        ReadSurveyByIdApiResponse,
        ReadSurveyByIdApiArg
      >({
        query: ({ surveyId }) => ({
          version: 1,
          path: `ratings/surveys/${surveyId}`,
          method: 'GET',
        }),
        providesTags: ['Surveys'],
      }),
      createSurvey: builder.mutation<
        CreateSurveyApiResponse,
        CreateSurveyApiArg
      >({
        query: ({ survey }) => ({
          version: 1,
          path: 'ratings/surveys',
          method: 'POST',
          body: survey,
        }),
        invalidatesTags: ['Surveys'],
      }),
      /**
       * The only direct mutation for surveys is renaming. Language-specific
       * details are updated through the language-specific endpoints.
       */
      updateSurveyName: builder.mutation<void, UpdateSurveyNameApiArg>({
        query: ({ surveyId, name }) => ({
          version: 1,
          path: `ratings/surveys/${surveyId}/name`,
          method: 'PUT',
          body: { name },
        }),
        invalidatesTags: ['Surveys'],
      }),
      deleteSurvey: builder.mutation<void, DeleteSurveyApiArg>({
        query: ({ surveyId }) => ({
          version: 1,
          path: `ratings/surveys/${surveyId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Surveys'],
      }),
      readSurveyTypes: builder.query<ReadSurveyTypesApiResponse, void>({
        query: () => ({
          version: 1,
          path: 'ratings/survey-types',
          method: 'GET',
        }),
      }),
      readSurveyLanguageSpecificDetails: builder.query<
        ReadSurveyLanguageSpecificDetailsApiResponse,
        ReadSurveyLanguageSpecificDetailsApiArg
      >({
        query: ({ surveyId, isoCode }) => ({
          version: 1,
          path: `ratings/surveys/${surveyId}/languages/${isoCode}`,
          method: 'GET',
        }),
        providesTags: ['SurveyLanguageSpecificDetails'],
      }),
      createOrUpdateSurveyLanguageSpecificDetails: builder.mutation<
        void,
        CreateOrUpdateSurveyLanguageSpecificDetailsApiArg
      >({
        query: ({
          surveyId,
          isoCode,
          linkLanguage: { type: _unusedType, ...details },
        }) => ({
          version: 1,
          path: `ratings/surveys/${surveyId}/languages/${isoCode}`,
          method: 'PUT',
          body: details,
        }),
        invalidatesTags: ['SurveyLanguageSpecificDetails'],
      }),
      deleteSurveyLanguageSpecificDetails: builder.mutation<
        void,
        DeleteSurveyLanguageSpecificDetailsApiArg
      >({
        query: ({ surveyId, isoCode }) => ({
          version: 1,
          path: `ratings/surveys/${surveyId}/languages/${isoCode}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['SurveyLanguageSpecificDetails'],
      }),
    }),
  });
